import React from 'react';
import { DeliverableCategory } from '@/types/deliverables';
import { cn } from '@/lib/utils';
import {
  Package,
  Wrench,
  GraduationCap,
  Settings,
  Headphones,
  FileText,
  Key,
  MoreHorizontal,
  FileSignature,
  FileCheck,
  Rocket,
  MessageSquare,
  Hammer
} from 'lucide-react';

interface DeliverableCategoryBadgeProps {
  category: DeliverableCategory;
  className?: string;
}

export const DeliverableCategoryBadge: React.FC<DeliverableCategoryBadgeProps> = ({ category, className }) => {
  const getCategoryConfig = (category: DeliverableCategory) => {
    switch (category) {
      case 'EQUIPMENT':
        return {
          label: 'Equipment',
          icon: Package,
          className: 'bg-purple-100 text-purple-700 border-purple-300'
        };
      case 'SERVICE':
        return {
          label: 'Service',
          icon: Wrench,
          className: 'bg-blue-100 text-blue-700 border-blue-300'
        };
      case 'TRAINING':
        return {
          label: 'Training',
          icon: GraduationCap,
          className: 'bg-green-100 text-green-700 border-green-300'
        };
      case 'INSTALLATION':
        return {
          label: 'Installation',
          icon: Settings,
          className: 'bg-orange-100 text-orange-700 border-orange-300'
        };
      case 'SUPPORT':
        return {
          label: 'Support',
          icon: Headphones,
          className: 'bg-yellow-100 text-yellow-700 border-yellow-300'
        };
      case 'DOCUMENTATION':
        return {
          label: 'Documentation',
          icon: FileText,
          className: 'bg-gray-100 text-gray-700 border-gray-300'
        };
      case 'LICENSE':
        return {
          label: 'License',
          icon: Key,
          className: 'bg-indigo-100 text-indigo-700 border-indigo-300'
        };
      case 'PROPOSAL':
        return {
          label: 'Proposal',
          icon: FileSignature,
          className: 'bg-cyan-100 text-cyan-700 border-cyan-300'
        };
      case 'CONTRACT':
        return {
          label: 'Contract',
          icon: FileCheck,
          className: 'bg-teal-100 text-teal-700 border-teal-300'
        };
      case 'IMPLEMENTATION':
        return {
          label: 'Implementation',
          icon: Rocket,
          className: 'bg-violet-100 text-violet-700 border-violet-300'
        };
      case 'CONSULTATION':
        return {
          label: 'Consultation',
          icon: MessageSquare,
          className: 'bg-rose-100 text-rose-700 border-rose-300'
        };
      case 'MAINTENANCE':
        return {
          label: 'Maintenance',
          icon: Hammer,
          className: 'bg-amber-100 text-amber-700 border-amber-300'
        };
      case 'OTHER':
        return {
          label: 'Other',
          icon: MoreHorizontal,
          className: 'bg-slate-100 text-slate-700 border-slate-300'
        };
      default:
        return {
          label: category,
          icon: MoreHorizontal,
          className: 'bg-gray-100 text-gray-700 border-gray-300'
        };
    }
  };

  const config = getCategoryConfig(category);
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border',
        config.className,
        className
      )}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};
